/**
 * Analytics domain types — the vocabulary shared by the `Site_Tracker` in the
 * SPA, the `Analytics_Ingest` and `Rollup_Job` Lambdas, and the Insights tab.
 *
 * Everything here is a type or a frozen value table: no behaviour, no I/O, and
 * no dependency beyond the other pure domain modules, so the same definitions
 * cross the public/owner boundary without duplication.
 *
 * Privacy shape of the data: an engagement event carries no IP address, no
 * cookie, no cross-site identifier and no free-form visitor text. Identity is
 * approximated by a per-Metric_Day, non-reversible `Visitor_Key` derived
 * server-side and stored in place of anything durable.
 */
/**
 * The kinds of engagement the `Site_Tracker` reports. This is the complete set:
 * an event whose type is not one of these is rejected at ingest as
 * {@link RejectionReason.UNKNOWN_EVENT_TYPE} and never reaches a metric.
 */
export const EngagementEventType = {
    /** A visit to the single-page site, carrying the referrer origin. */
    PAGE_VIEW: "page_view",
    /** A hash navigation to a section of the site. */
    SECTION_VIEW: "section_view",
    /** A portfolio image opened in the lightbox. */
    IMAGE_OPEN: "image_open",
    /** A nav link, CTA or external footer link followed. */
    CLICK_THROUGH: "click_through",
    /** The first edit to the booking form in a session. */
    BOOKING_FORM_STARTED: "booking_form_started",
    /** A booking inquiry successfully submitted. */
    BOOKING_SUBMITTED: "booking_submitted",
};
/**
 * Screen-width buckets — the only device signal collected. Deliberately coarse
 * (three buckets, no pixel value) so it cannot contribute to a device
 * fingerprint.
 */
export const ScreenWidthBucket = { SM: "sm", MD: "md", LG: "lg" };
/**
 * Where a visit came from, as shown to the Owner. Classified at rollup/read
 * time from the stored referrer origin, so correcting the host table
 * reclassifies past days too.
 */
export const TrafficSource = {
    INSTAGRAM: "Instagram",
    TIKTOK: "TikTok",
    FACEBOOK: "Facebook",
    GOOGLE: "Google",
    BING: "Bing",
    /** No referrer at all. */
    DIRECT: "Direct",
    /** The referrer is a configured site host of this environment. */
    SAME_SITE: "Same site",
    /** Parsable but unrecognised, or unparsable. */
    OTHER: "Other",
};
/**
 * Why an event or a whole request was not recorded. Counted per Metric_Day and
 * per reason so the Owner can see collection problems; a rejected event never
 * contributes to any metric.
 */
export const RejectionReason = {
    UNKNOWN_EVENT_TYPE: "unknown_event_type",
    MALFORMED_EVENT: "malformed_event",
    DUPLICATE_EVENT: "duplicate_event",
    BOT_USER_AGENT: "bot_user_agent",
    DISALLOWED_ORIGIN: "disallowed_origin",
    BATCH_TOO_LARGE: "batch_too_large",
    BODY_TOO_LARGE: "body_too_large",
    MALFORMED_JSON: "malformed_json",
    RATE_LIMITED: "rate_limited",
    DAILY_CAP: "daily_cap",
    PERSIST_FAILED: "persist_failed",
};
//# sourceMappingURL=analytics.js.map